import React from 'react';
import { Typography, Button, Box, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { parseMessage } from '../utils/messageParser';

const useStyles = makeStyles((theme) => ({
  menuButton: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(1),
    textTransform: 'none',
    borderRadius: '8px',
    padding: theme.spacing(1, 2),
    border: `1px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.background.paper,
    fontSize: '14px',
    fontWeight: 500,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.getContrastText(theme.palette.primary.main),
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(33, 57, 94, 0.3)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  },
  menuButtonsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
  },
  messageText: {
    fontSize: '15px',
    lineHeight: '1.6',
    whiteSpace: 'pre-wrap',
  },
}));

interface MessageContentProps {
  text: string;
  isStreaming?: boolean;
  onMenuItemClick?: (menuItem: string) => void;
  streamingIndicator?: React.ReactNode;
}

/**
 * Component that renders a chat message with support for clickable menu items.
 * Menu items are lines starting with * in the message text.
 */
export const MessageContent: React.FC<MessageContentProps> = ({
  text,
  isStreaming,
  onMenuItemClick,
  streamingIndicator,
}) => {
  const classes = useStyles();
  const { mainText, menuItems } = parseMessage(text);
  const showMenuButtons = menuItems.length > 0 && !isStreaming;

  return (
    <>
      <Typography className={classes.messageText}>
        {mainText}
        {isStreaming && streamingIndicator}
      </Typography>
      {showMenuButtons && (
        <Box className={classes.menuButtonsContainer}>
          {menuItems.map((item, index) => {
            const button = (
              <Button
                key={index}
                className={classes.menuButton}
                onClick={() => onMenuItemClick?.(item.text)}
                variant="outlined"
                size="small"
              >
                {item.text}
              </Button>
            );

            return item.tooltip ? (
              <Tooltip key={index} title={item.tooltip} arrow placement="top">
                {button}
              </Tooltip>
            ) : (
              button
            );
          })}
        </Box>
      )}
    </>
  );
};
